/**
 * Localization utility for the Autobot Competency Analyzer
 * Provides translations for all UI text in English and French
 * Uses localStorage for language settings
 */

import { createErrorLogger, ErrorType } from '../errorHandler';
import LocalStorageService from '../localStorageService';
import { LANGUAGES, LANGUAGE_KEY, DEFAULT_LANGUAGE } from './config';
import { translations } from './translations';

const logger = createErrorLogger('Localization');

// Current language - defaults to French
let currentLanguage = DEFAULT_LANGUAGE;

/**
 * Get a translation by key path (e.g. "common.save")
 * @param {string} keyPath - The key path to the translation
 * @param {object} params - Optional parameters to replace in the translation
 * @returns {string} The translation in the current language
 */
export const t = (keyPath, params = null) => {
  if (!keyPath || typeof keyPath !== 'string') {
    logger.warn('t', 'Invalid keyPath provided', { keyPath });
    return '';
  }

  const keys = keyPath.split('.');

  if (keys.length < 2) {
    logger.warn('t', `Invalid key format: ${keyPath}. Expected format: section.key or section.subkey.key`, { keyPath });
    return keyPath;
  }

  const [section, ...nestedKeys] = keys;
  const lang = currentLanguage;

  // Check if section exists in translations
  if (!translations[section]) {
    logger.warn('t', `Translation section not found: ${section}`, { section, keyPath });
    return keyPath;
  }

  const sectionTranslations = translations[section];

  // Check if language exists for this section
  if (!sectionTranslations[lang]) {
    logger.warn('t', `Language not found for section: ${section}`, { section, lang, keyPath });
    return keyPath;
  }

  // Navigate through nested keys to get the translation
  let translation = sectionTranslations[lang];
  for (const key of nestedKeys) {
    if (translation && typeof translation === 'object' && translation[key] !== undefined) {
      translation = translation[key];
    } else {
      translation = null;
      break;
    }
  }

  if (!translation) {
    return keyPath;
  }

  // Process parameters if provided
  if (params && typeof params === 'object') {
    let result = translation;
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      const regex = new RegExp(`\\{${paramKey}\\}`, 'g');
      result = result.replace(regex, paramValue);
    });
    return result;
  }

  return translation;
};

/**
 * Set the current language
 * @param {string} language - Language code (e.g. "en", "fr")
 * @returns {Promise<boolean>} Success status
 */
export const setLanguage = async (language) => {
  if (Object.values(LANGUAGES).includes(language)) {
    currentLanguage = language;

    // Store the language preference in localStorage
    try {
      LocalStorageService.setItem(LANGUAGE_KEY, language);
      logger.info('setLanguage', `Language set to ${language} and saved to localStorage`, { language });
    } catch (error) {
      logger.error('setLanguage', 'Error saving language preference', error, ErrorType.DATA_SAVE);
    }

    // Dispatch an event for components to know language has changed
    const event = new CustomEvent('languageChanged', { detail: { language } });
    window.dispatchEvent(event);

    return true;
  } else {
    logger.warn('setLanguage', `Language not supported: ${language}`, { language });
    return false;
  }
};

/**
 * Get the current language
 * @returns {string} The current language code
 */
export const getLanguage = () => {
  return currentLanguage;
};

/**
 * Initialize language from localStorage or browser settings
 * @returns {Promise<void>}
 */
export const initLanguage = async () => {
  try {
    // Try to get language from localStorage first
    const storedLanguage = LocalStorageService.getItem(LANGUAGE_KEY);

    if (storedLanguage && Object.values(LANGUAGES).includes(storedLanguage)) {
      currentLanguage = storedLanguage;
      logger.info('initLanguage', `Loaded language from localStorage: ${storedLanguage}`, { storedLanguage });
      return;
    }

    // Default to French as per requirements (unless user explicitly prefers English)
    const _browserLanguage = navigator.language.substring(0, 2).toLowerCase();

    // Only use English if browser language is explicitly English, otherwise default to French
    currentLanguage = LANGUAGES.FR; // Default to French

    // Note: Uncomment the line below if you want to respect browser language preference
    // if (browserLanguage === 'en') currentLanguage = LANGUAGES.EN;

    logger.info('initLanguage', `Using browser language: ${currentLanguage}`, { currentLanguage });

    // Save the detected language to localStorage
    LocalStorageService.setItem(LANGUAGE_KEY, currentLanguage);
  } catch (error) {
    logger.error('initLanguage', 'Error initializing language', error, ErrorType.GENERAL);
    throw error;
  }
};

// Initialize language when this file is imported
// Using an async IIFE to handle the async initLanguage function
(async () => {
  try {
    await initLanguage();
    logger.info('init', `Language initialized to: ${currentLanguage}`, { currentLanguage });
  } catch (err) {
    logger.error('init', 'Failed to initialize language', err, ErrorType.GENERAL);
  }
})();

// Export a hook for React components
export const useTranslation = () => {
  return { t, setLanguage, getLanguage, LANGUAGES };
};

// Re-export for backward compatibility
export { LANGUAGES, translations };
